import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, updateDoc, arrayUnion, deleteDoc, doc, where, Timestamp, FieldPath, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase-client';
import { isRecoverableFirestoreListenTargetError, safeOnSnapshot } from '../../../utils/firestoreSubscriptions';
import { reportCaughtError } from '../../../utils/clientErrorReporting';
import { timestampToMillis } from '../threadState';
import { useOnlineRefreshSignal, useOnlineStatus } from '../../../utils/onlineStatus';
import { readOfflineContentCache, writeOfflineContentCache } from '../../../utils/offlineContentCache';

const CACHE_SCOPE = 'messages';

function isPermissionDeniedError(error) {
  return String(error?.code || '').toLowerCase() === 'permission-denied';
}

async function clearThreadNotifications(currentUserId, threadId) {
  const notifRef = collection(db, 'notifications', currentUserId, 'items');
  const notifQuery = query(notifRef, where('actionUrl', '==', `/messages/${threadId}`));
  const notifSnap = await getDocs(notifQuery);
  const deletes = [
    deleteDoc(doc(db, 'notifications', currentUserId, 'items', threadId)),
    ...notifSnap.docs.map(d => deleteDoc(d.ref))
  ];
  await Promise.all(deletes);
}

async function markSnapshotRead(snapshotDocs, currentUserId) {
  const updates = snapshotDocs
    .map((d) => {
      const data = d.data() || {};
      if (!Array.isArray(data.readBy) || data.readBy.includes(currentUserId)) {
        return null;
      }

      const updateArgs = [
        d.ref,
        'readBy',
        arrayUnion(currentUserId),
      ];
      const readAt = data.readAt;
      if (
        !readAt
        || (
          typeof readAt === 'object'
          && !Array.isArray(readAt)
          && !Object.prototype.hasOwnProperty.call(readAt, currentUserId)
        )
      ) {
        updateArgs.push(
          new FieldPath('readAt', currentUserId),
          serverTimestamp()
        );
      }
      return updateDoc(...updateArgs);
    })
    .filter(Boolean);
  await Promise.all(updates);
}

/**
 * useMessages hook
 *
 * Subscribes to messages for a given threadId, returns the list of messages,
 * marks unread messages as read, and clears related notifications.
 *
 * @param {string} threadId - ID of the message thread
 * @param {string} currentUserId - UID of the current user
 * @param {object|null} deletedBefore - per-user delete cutoff timestamp
 * @param {Array<string>} threadParticipants - users allowed to write direct message read receipts
 * @returns {{ messages: Array }}
 */
export default function useMessages(threadId, currentUserId, deletedBefore = null, routeContext = {}, threadParticipants = []) {
  const [messages, setMessages] = useState([]);
  const online = useOnlineStatus();
  const onlineRefreshSignal = useOnlineRefreshSignal();
  const deletedBeforeMillis = timestampToMillis(deletedBefore);
  const canWriteReadReceipts = Boolean(
    currentUserId
    && Array.isArray(threadParticipants)
    && threadParticipants.includes(currentUserId)
  );

  // Subscribe to messages in ascending order
  useEffect(() => {
    if (!threadId) {
      setMessages([]);
      return;
    }
    const cacheParts = [currentUserId || 'unknown', CACHE_SCOPE, 'threadMessages', threadId];
    setMessages(readOfflineContentCache(cacheParts, []));
    const msgsRef = collection(db, 'messages', threadId, 'messages');
    const deletedBeforeQueryValue = deletedBeforeMillis
      ? Timestamp.fromMillis(deletedBeforeMillis)
      : null;
    const msgsQuery = deletedBeforeMillis
      ? query(msgsRef, where('sentAt', '>', deletedBeforeQueryValue), orderBy('sentAt', 'asc'))
      : query(msgsRef, orderBy('sentAt', 'asc'));
    const unsub = safeOnSnapshot(msgsQuery, snap => {
      const msgs = snap.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));
      setMessages(msgs);
      writeOfflineContentCache(cacheParts, msgs.slice(-200));
      if (canWriteReadReceipts && !snap.metadata?.fromCache && online) {
        (async () => {
          try {
            await markSnapshotRead(snap.docs, currentUserId);
          } finally {
            await clearThreadNotifications(currentUserId, threadId);
          }
        })().catch((err) => {
          if (isRecoverableFirestoreListenTargetError(err)) return;
          if (isPermissionDeniedError(err)) return;
          reportCaughtError(err, {
            source: 'messages.direct.markReadAndClearNotifications',
            severity: 'warning',
            context: {
              threadId,
              uid: currentUserId,
              ...routeContext,
            },
          });
        });
      }
    }, err => void 0, { label: 'messages.threadMessages', threadId, uid: currentUserId || '', ...routeContext });
    return () => unsub();
  }, [threadId, currentUserId, deletedBefore, deletedBeforeMillis, online, onlineRefreshSignal, routeContext, canWriteReadReceipts]);

  // Keep clearing notifications that are created after the message snapshot arrives.
  useEffect(() => {
    if (!threadId || !currentUserId) return undefined;
    if (!online) return undefined;

    const notifRef = collection(db, 'notifications', currentUserId, 'items');
    const notifQuery = query(notifRef, where('actionUrl', '==', `/messages/${threadId}`));
    const unsub = safeOnSnapshot(notifQuery, snap => {
      if (snap.empty) return;
      Promise.all(snap.docs.map(d => deleteDoc(d.ref))).catch((err) => {
        if (isRecoverableFirestoreListenTargetError(err)) return;
        if (isPermissionDeniedError(err)) return;
        reportCaughtError(err, {
          source: 'messages.direct.clearNotificationsSubscription',
          severity: 'warning',
          context: {
            threadId,
            uid: currentUserId,
            ...routeContext,
          },
        });
      });
    }, () => {}, { label: 'messages.directNotifications', threadId, uid: currentUserId, ...routeContext });

    clearThreadNotifications(currentUserId, threadId).catch((err) => {
      if (isRecoverableFirestoreListenTargetError(err)) return;
      if (isPermissionDeniedError(err)) return;
      reportCaughtError(err, {
        source: 'messages.direct.clearNotificationsInitial',
        severity: 'warning',
        context: {
          threadId,
          uid: currentUserId,
          ...routeContext,
        },
      });
    });
    return () => unsub();
  }, [threadId, currentUserId, online, routeContext]);
  return {
    messages
  };
}
